import { formatDateToISOCustom } from "../dateUtils";

const safeDate = (d) => {
  if (!d) return "";
  const v = formatDateToISOCustom(d);
  if (!v || v.startsWith("0001")) return "";
  return v;
};

export function buildCSV(dataObj) {

  const backend = dataObj.applicants || [];

  const maxRounds = backend.reduce(
    (m, c) => Math.max(m, Array.isArray(c.rounds) ? c.rounds.length : 0),
    0
  );

  const baseStart = [
    "Name", "Email", "Phone", "Applied On", "Diversity", "Total Experience",
    "Highest Education", "Grade", "Time To Join", "Current Salary", "Expected Salary",
    "Current Status", "Rejection Reason", "Current location", "Preffered location", "6 Days Willingness", "Willing to Relocate (Hyd)", "7 Days Roster Willingness", "Resume Score", "AI Interview Status", "AI Interviewed Date",
    "Aggregate AI Score", "Employability Skills Score", "Technical Skills Score",
    "Voice Score", "Average Interview Rating"
  ];

  const roundHeaders = [];
  for (let i = 1; i <= maxRounds; i++) {
    roundHeaders.push(
      `Round${i} Date`,
      `Round${i} Interviewer`,
      `Round${i} Outcome`,
      `Round${i} Rating`,
      `Round${i} Feedback`
    );
  }

  const baseEnd = [
    "Join Date",
    "Salary Proposal Date",
    "Offer Approval Created At",
    "Offer Acknowledgement Status",
    "Offer Acknowledgement Date"
  ];

  const orderedHeaders = [...baseStart, ...roundHeaders, ...baseEnd];
  const headers = orderedHeaders.map((h) => ({ label: h, key: h }));

  const data = backend.map((c) => {
    const row = {
      "Name": c.name || "",
      "Email": c.email || "",
      "Phone": c.phone || "",
      "Applied On": safeDate(c.applied_on),
      "Diversity": c.diversity || "",
      "Total Experience": c.total_experience || "",
      "Highest Education": c.highest_education || "",
      "Grade": c.grade || "",
      "Time To Join": c.time_to_join || "",
      "Current Salary": c.current_salary || "",
      "Expected Salary": c.expected_salary || "",
      "Current Status": c.current_status || "",
      "6 Days Willingness": c.willing_to_work_6_days || "",
      "Willing to Relocate (Hyd)": c.willing_to_relocate_hyderabad || "",
      "7 Days Roster Willingness": c.comfortable_7_day_roster || "",
      "Resume Score": c.resume_score ?? "",
      "AI Interview Status": c.ai_interview_status || "",
      "AI Interviewed Date": safeDate(c.ai_interviewed_date),
      "Aggregate AI Score": c.aggregate_ai_score ?? "",
      "Employability Skills Score": c.employability_skills_score ?? "",
      "Technical Skills Score": c.technical_skills_score ?? "",
      "Voice Score": c.voice_score ?? "",
      "Average Interview Rating": c.average_interview_rating ?? "",
      "Current location": c.current_location ?? "",
      "Preffered location": c.preferred_location ?? "",
      "Rejection Reason": c.rejection_reason || "",
    };

    for (let i = 1; i <= maxRounds; i++) {
      const r = c.rounds?.[i - 1] || {};
      row[`Round${i} Date`] = safeDate(r.date);
      row[`Round${i} Interviewer`] = r.interviewer || "";
      row[`Round${i} Outcome`] = r.outcome || "";
      row[`Round${i} Rating`] = r.rating ?? "";
      row[`Round${i} Feedback`] = r.feedback || "";
    }

    row["Join Date"] = safeDate(c.join_date);
    row["Salary Proposal Date"] = safeDate(c.salary_proposal_date);
    row["Offer Approval Created At"] = safeDate(c.offer_approval_created_at);
    row["Offer Acknowledgement Status"] = c.offer_acknowledgement_status || "";
    row["Offer Acknowledgement Date"] = safeDate(c.offer_acknowledgement_date);

    return row;
  });

  return { data, headers };
}

export function buildCSVExpandingRows(dataObj) {

  const backend = dataObj.applicants || [];

  const maxRounds = backend.reduce(
    (m, c) => Math.max(m, Array.isArray(c.rounds) ? c.rounds.length : 0),
    0
  );

  // Detect SocketAI data: collect unique skill keys from skill_experiences
  const skillKeys = [];
  let hasOfficeWillingness = false;
  let hasNoticePeriod = false;
  backend.forEach((c) => {
    if (c.willing_to_work_5_days_office !== undefined && c.willing_to_work_5_days_office !== null) {
      hasOfficeWillingness = true;
    }
    if (c.noticeperiod) hasNoticePeriod = true;
    if (c.skill_experiences && typeof c.skill_experiences === "object") {
      Object.keys(c.skill_experiences).forEach((k) => {
        if (!skillKeys.includes(k)) skillKeys.push(k);
      });
    }
  });

  const socketAIColumns = [
    ...(hasOfficeWillingness ? ["Office Willingness"] : []),
    ...(hasNoticePeriod ? ["Notice Period (days)"] : []),
    ...skillKeys.map((k) => `Exp in ${k} (yrs)`),
  ];

  const baseStart = [
    "Name", "Email", "Phone", "Applied On", "Diversity", "Total Experience",
    "Highest Education", "Grade", "6 Days Willingness", "Willing to Relocate (Hyd)", "7 Days Roster Willingness", "EdTech Experience", "Laptop & WiFi",
    "Time To Join", "Current Salary", "Expected Salary",
    ...socketAIColumns,
    "Current Status","Rejection Reason", "Current location", "Preffered location", "Resume Score",
    "AI Interview Status", "AI Interviewed Date",
    "Aggregate AI Score", "Employability Skills Score", "Technical Skills Score",
    "Voice Score", "Average Interview Rating"
  ];

  const roundHeaders = [];
  for (let i = 1; i <= maxRounds; i++) {
    roundHeaders.push(
      `Round${i} Date`,
      `Round${i} Interviewer`,
      `Round${i} Outcome`,
      `Round${i} Rating`,
      `Round${i} Feedback`
    );
  }

  const baseEnd = [
    "Join Date",
    "Salary Proposal Date",
    "Offer Approval Created At",
    "Offer Acknowledgement Status",
    "Offer Acknowledgement Date"
  ];

  const orderedHeaders = [...baseStart, ...roundHeaders, ...baseEnd];
  const headers = orderedHeaders.map((h) => ({ label: h, key: h }));

  const data = backend.map((c) => {
    const row = {
      "Name": c.name || "",
      "Email": c.email || "",
      "Phone": c.phone || "",
      "Applied On": safeDate(c.applied_on),
      "Diversity": c.diversity || "",
      "Total Experience": c.total_experience || "",
      "Highest Education": c.highest_education || "",
      "Grade": c.grade || "",
      "6 Days Willingness": c.willing_to_work_6_days || "",
      "Willing to Relocate (Hyd)": c.willing_to_relocate_hyderabad || "",
      "7 Days Roster Willingness": c.comfortable_7_day_roster || "",
      "EdTech Experience": c.edtech_experience || "",
      "Laptop & WiFi": c.remote_work || "",
      "Time To Join": c.time_to_join || "",
      "Current Salary": c.current_salary || "",
      "Expected Salary": c.expected_salary || "",
      "Current Status": c.current_status || "",
      "Resume Score": c.resume_score ?? "",
      "AI Interview Status": c.ai_interview_status || "",
      "AI Interviewed Date": safeDate(c.ai_interviewed_date),
      "Aggregate AI Score": c.aggregate_ai_score ?? "",
      "Employability Skills Score": c.employability_skills_score ?? "",
      "Technical Skills Score": c.technical_skills_score ?? "",
      "Voice Score": c.voice_score ?? "",
      "Average Interview Rating": c.average_interview_rating ?? "",
      "Current location": c.current_location ?? "",
      "Preffered location": c.preferred_location ?? "",
    };

    if (hasOfficeWillingness) {
      row["Office Willingness"] = c.willing_to_work_5_days_office ? "Yes" : "No";
    }
    if (hasNoticePeriod) {
      row["Notice Period (days)"] = c.noticeperiod || "";
    }
    skillKeys.forEach((k) => {
      row[`Exp in ${k} (yrs)`] =
        c.skill_experiences && c.skill_experiences[k] != null
          ? c.skill_experiences[k]
          : "";
    });

    for (let i = 1; i <= maxRounds; i++) {
      const r = c.rounds?.[i - 1] || {};
      row[`Round${i} Date`] = safeDate(r.date);
      row[`Round${i} Interviewer`] = r.interviewer || "";
      row[`Round${i} Outcome`] = r.outcome || "";
      row[`Round${i} Rating`] = r.rating ?? "";
      row[`Round${i} Feedback`] = r.feedback || "";
    }

    row["Join Date"] = safeDate(c.join_date);
    row["Salary Proposal Date"] = safeDate(c.salary_proposal_date);
    row["Offer Approval Created At"] = safeDate(c.offer_approval_created_at);
    row["Offer Acknowledgement Status"] = c.offer_acknowledgement_status || "";
    row["Offer Acknowledgement Date"] = safeDate(c.offer_acknowledgement_date);

    return row;
  });

  return { data, headers };
}
